<template>
  <div v-if="loggedIn" class="anz-bg-black-3 anz-p-l">
    <div class="anz-flex anz-justify-content-between anz-mb-m">
      <a-button
        add-href
        :href="generateProfileRoute(currentUser)"
        class-name="anz-link anz-flex anz-align-items-center"
      >
        <template #title>
          <span class="anz-link-hover-underline">
            <a-icon
              name="user"
              width="0.8571em"
              height="0.8571em"
              class-name="anz-mr-xs"
            />
            <strong class="anz-strong anz-ml-xxs anz-mr-xs">{{
              currentUser.nickname
            }}</strong>
          </span>
          <a-premium
            size="anz-badge-md"
            v-if="currentUser.isSubscriptionPremium"
          />
        </template>
      </a-button>
      <a-button
        @click="expandList = !expandList"
        className="anz-link anz-ml-xs anz-flex-mo anz-align-items-center anz-hidden-sm"
      >
        <template #title>
          <template v-if="expandList">
            <strong class="anz-strong anz-fs-xxxs">{{
              $translate('profile.crossroads.close')
            }}</strong>
            <a-icon
              name="chevron-up"
              width="1.428em"
              height="1.071em"
              class-name="anz-color-black-50 anz-mt-xxs"
              :style="{ opacity: '0.4' }"
            />
          </template>
          <template v-else>
            <strong class="anz-strong anz-fs-xxxs">{{
              $translate('profile.crossroads.more')
            }}</strong>
            <a-icon
              name="chevron-down"
              width="1.428em"
              height="1.071em"
              class-name="anz-color-black-50 anz-mt-xxs"
              :style="{ opacity: '0.4' }"
            />
          </template>
        </template>
      </a-button>
    </div>
    <div class="anz-color-black-40 anz-text-uppercase anz-mb-xs anz-fs-12">
      {{ $translate('profile.crossroads.section.subscription') }}
    </div>
    <a-section-list>
      <a-section-list-item
        v-if="currentUser.isPayingCustomer && currentUser.isSubscriptionPremium"
        :href="generateCentralProfileRote(CROSSROADS_SUBSCRIPTION_TYPE)"
      >
        <template #content-title>
          {{ $translate('profile.crossroads.subscription.activeSubscription') }}
        </template>
        <template #content-value>
          {{
            $translate(
              'profile.crossroads.subscription.premiumSubscriptionType'
            )
          }}
        </template>
      </a-section-list-item>
      <a-section-list-item
        v-else-if="currentUser.isPayingCustomer"
        :href="generateCentralProfileRote(CROSSROADS_SUBSCRIPTION_TYPE)"
      >
        <template #content-title>
          {{ $translate('profile.crossroads.subscription.activeSubscription') }}
        </template>
        <template #content-value>
          {{
            $translate(
              'profile.crossroads.subscription.standardSubscriptionType'
            )
          }}
        </template>
      </a-section-list-item>
      <a-section-list-item
        v-else
        :href="generateCentralProfileRote(CROSSROADS_SUBSCRIPTION_TYPE)"
      >
        <template #content-title>
          {{ $translate('profile.crossroads.subscription.noSubscription') }}
        </template>
        <template #content-value>
          {{ $translate('profile.crossroads.subscription.upgrade') }}
        </template>
      </a-section-list-item>
    </a-section-list>
    <template v-if="!currentUser.isSubscriptionPremium">
      <div
        class="anz-align-center anz-fs-12 anz-lh-165 anz-color-black-50 anz-mtn-s"
        :class="{ 'anz-hidden-mo': !expandList }"
      >
        {{ $translate('profile.crossroads.subscription.upgradeToPremiumText')
        }}<br />
        <a-button
          add-href
          :href="
            generateBuyPremiumSubscriptionRoute(CROSSROADS_SUBSCRIPTION_OFFER)
          "
          :title="
            '(' +
              $translate(
                'profile.crossroads.subscription.upgradeToPremiumOffer'
              ) +
              ')'
          "
          className="anz-link anz-color-primary anz-link-hover-underline"
        />
      </div>
      <hr
        class="anz-hr anz-mt-s anz-mb-l"
        :class="{ 'anz-hidden-mo': !expandList }"
      />
    </template>
    <div class="anz-color-black-40 anz-text-uppercase anz-mb-xs anz-fs-12">
      {{ $translate('profile.crossroads.section.forum') }}
    </div>
    <div class="anz-flex anz-mxn-xxs anz-mb-xs">
      <a-button
        add-href
        :href="
          currentUser.stats.postRepliesUnseen > 0
            ? generateUnseenRepliesRoute(currentUser)
            : generateAllRepliesRoute(currentUser)
        "
        className="anz-flex-fill anz-bg-white anz-align-center anz-link anz-py-s anz-px-m anz-mx-xxs"
      >
        <template #title>
          <strong class="anz-color-primary anz-strong anz-fs-16">{{
            currentUser.stats.postRepliesUnseen
          }}</strong>
          <br />
          <span class="anz-fs-12">{{
            $translate('profile.crossroads.postRepliesUnseen')
          }}</span>
        </template>
      </a-button>
      <a-button
        add-href
        :href="generateMyPostsRoute(currentUser)"
        className="anz-flex-fill anz-bg-white anz-align-center anz-link anz-py-s anz-px-m anz-mx-xxs"
      >
        <template #title>
          <strong class="anz-color-primary anz-strong anz-fs-16">
            {{ currentUser.stats.visiblePosts }}
          </strong>
          <br />
          <span class="anz-fs-12">
            {{ $translate('profile.crossroads.posts') }}
          </span>
        </template>
      </a-button>
    </div>
    <div :class="{ 'anz-hidden-mo': !expandList }">
      <a-section-list>
        <a-section-list-item :href="generateShowReportedPosts(currentUser)">
          <template #content-title>
            {{ $translate('profile.crossroads.reportedPosts') }}
          </template>
          <template #content-value>
            {{ currentUser.stats.reportedPostsAll }}
          </template>
        </a-section-list-item>
        <a-section-list-item :href="generateSubscribersRoute(currentUser)">
          <template #content-title>
            {{ $translate('profile.crossroads.subscribers') }}
          </template>
          <template #content-value>
            {{ currentUser.stats.subscribedUsers }}
          </template>
        </a-section-list-item>
        <a-section-list-item :href="generateFollowersRoute(currentUser)">
          <template #content-title>
            {{ $translate('profile.crossroads.followers') }}
          </template>
          <template #content-value>
            {{ currentUser.stats.followers }}
          </template>
        </a-section-list-item>
      </a-section-list>
    </div>
    <div :class="{ 'anz-hidden-mo': !expandList }">
      <div class="anz-color-black-40 anz-text-uppercase anz-mb-xs anz-fs-12">
        {{ $translate('profile.crossroads.section.awards') }}
      </div>
      <div>
        <a-section-list>
          <a-section-list-item :href="generateSuperLikesRoute(currentUser)">
            <template #content-title>
              {{ $translate('profile.crossroads.awards.superLikesAvailable') }}
            </template>
            <template #content-value>
              <div class="anz-flex anz-align-items-center">
                <a-icon
                  name="star"
                  width="0.875em"
                  height="0.875em"
                  class-name="anz-mr-xxs anz-color-orange"
                />
                <span class="anz-text-nowrap">
                  {{ currentUser.stats.superLikesRemaining }} /
                  {{ currentUser.stats.superLikesEntitled }}
                </span>
              </div>
            </template>
          </a-section-list-item>
        </a-section-list>
      </div>
    </div>
    <template
      v-if="
        !currentUser.isSubscriptionPremium &&
          currentUser.stats.superLikesRemaining === 0
      "
    >
      <div
        class="anz-align-center anz-fs-12 anz-lh-165 anz-color-black-50 anz-mtn-xs"
        :class="{ 'anz-hidden-mo': !expandList }"
      >
        {{ $translate('profile.crossroads.awards.standardUserExceededTextA') }}
        <a-button
          add-href
          :href="
            generateBuyPremiumSubscriptionRoute(CROSSROADS_SUPER_LIKE_OFFER)
          "
          :title="
            $translate(
              'profile.crossroads.awards.standardUserExceededBuyPremium'
            )
          "
          className="anz-link anz-color-primary anz-link-hover-underline"
        />
      </div>
      <hr
        class="anz-hr anz-mt-s anz-mb-m"
        :class="{ 'anz-hidden-mo': !expandList }"
      />
    </template>
    <div :class="{ 'anz-hidden-mo': !expandList }">
      <div
        class="anz-flex anz-align-items-center anz-justify-content-center anz-mb-xs"
      >
        <a-button
          add-href
          :href="generateProfileRoute(currentUser)"
          :title="$translate('profile.crossroads.discussionProfile')"
          className="anz-link anz-strong anz-fs-xxxs anz-link-hover-underline"
        />
        <span class="anz-mx-s anz-color-gray-300 anz-mtn-xxs">|</span>
        <a-button
          add-href
          :href="generateUserEditRoute()"
          :title="$translate('profile.crossroads.profile')"
          className="anz-link anz-strong anz-fs-xxxs anz-link-hover-underline"
        />
      </div>
      <div class="anz-align-center anz-color-black-50 anz-fs-12 anz-lh-165">
        {{ $translate('profile.crossroads.codexAccompanyingText') }}
        <a-button
          add-href
          :href="generateCodexRoute()"
          :title="'(' + $translate('profile.crossroads.codex') + ')'"
          className="anz-link anz-color-primary anz-link-hover-underline"
        />
      </div>
    </div>
  </div>
  <div></div>
</template>

<script>
import AButton from '@/components/Forms/AButton.vue'
import AIcon from '@/components/AIcon'
import useAppState from '@/composables/ui/ui'
import useCurrentUser from '@/composables/user/user'
import { useRouter } from '@/routing/router'
import APremium from '@/components/Ui/Badge/Premium'
import ASectionList from '@/components/Ui/Crossroads/SectionList'
import ASectionListItem from '@/components/Ui/Crossroads/SectionListItem'
import { OfferLinkPosition } from '@/routing/refManager'

export default {
  name: 'ProfileCrossroads',
  components: {
    ASectionListItem,
    ASectionList,
    APremium,
    AButton,
    AIcon,
  },
  emits: [],
  data() {
    return {
      expandList: false,
      CROSSROADS_SUBSCRIPTION_TYPE:
        OfferLinkPosition.CROSSROADS_SUBSCRIPTION_TYPE,
      CROSSROADS_SUBSCRIPTION_OFFER:
        OfferLinkPosition.CROSSROADS_SUBSCRIPTION_OFFER,
      CROSSROADS_SUPER_LIKE_OFFER:
        OfferLinkPosition.CROSSROADS_SUPER_LIKE_OFFER,
    }
  },
  methods: {},
  setup() {
    const { loggedIn } = useAppState()
    const { currentUser } = useCurrentUser()

    return {
      loggedIn,
      currentUser,

      ...useRouter(),
    }
  },
}
</script>

<style></style>
